/**
 * Expandable prompt-details panel for one payer card.
 *
 * Lets the operator upload a prompt JSON (delegated to the parent via
 * {@link Props.onFileSelected}) and preview / download the currently saved
 * prompt for the payer.
 * @module PayerPromptDetails
 */
import { useState } from "react";

type Dict = Record<string, unknown>;

interface Props {
  /** Payer name, used for the download filename. */
  name: string;
  /** Saved `j_code_info` prompt section. */
  jcode: Dict;
  /** Saved `prompt_provider_info` prompt section. */
  provider: Dict;
  /** Handles a newly picked prompt JSON file. */
  onFileSelected: (file: File | undefined) => void;
}

/**
 * Renders the upload control and current-prompt preview for a payer.
 *
 * @param props - Payer prompt sections and file handler
 */
const PayerPromptDetails = ({
  name,
  jcode,
  provider,
  onFileSelected,
}: Props) => {
  const [showCurrent, setShowCurrent] = useState(false);

  const downloadCurrent = () => {
    const blob = new Blob(
      [
        JSON.stringify(
          { j_code_info: jcode, prompt_provider_info: provider },
          null,
          2,
        ),
      ],
      { type: "application/json" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name.replace(/\s+/g, "_")}_prompt.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-4 border-t border-primaryGray-14 p-4">
      <div className="flex flex-col gap-2">
        <div className="text-sm font-semibold text-primaryGray-1">
          Upload prompt JSON
        </div>
        <div className="text-xs text-primaryGray-6">
          Pick a JSON file with{" "}
          <code className="rounded bg-primaryGray-16 px-1">j_code_info</code>{" "}
          and{" "}
          <code className="rounded bg-primaryGray-16 px-1">
            prompt_provider_info
          </code>{" "}
          for this payer. You’ll choose which orgs to save it to next.
        </div>
        <input
          type="file"
          accept="application/json,.json"
          onChange={(e) => {
            onFileSelected(e.target.files?.[0]);
            e.target.value = "";
          }}
          className="text-xs text-primaryGray-6"
        />
      </div>

      <div className="rounded-lg border border-primaryGray-14 bg-primaryGray-16 p-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCurrent((v) => !v)}
            className="text-sm font-semibold text-primaryGray-6 hover:text-primaryGray-1"
          >
            {showCurrent ? "▾" : "▸"} Current saved prompt
          </button>
          <button
            onClick={downloadCurrent}
            className="ml-auto rounded border border-primaryGray-14 px-2 py-1 text-tiny font-semibold text-primaryGray-1 hover:bg-white"
          >
            Download JSON
          </button>
        </div>
        {showCurrent && (
          <pre className="mt-2 max-h-72 overflow-auto rounded border border-primaryGray-14 bg-white p-2 text-xs text-primaryGray-1">
            {JSON.stringify(
              { j_code_info: jcode, prompt_provider_info: provider },
              null,
              2,
            )}
          </pre>
        )}
      </div>
    </div>
  );
};

export default PayerPromptDetails;
